import { useState, useEffect, useRef } from 'react';
import { Bell, CheckCircle2, Clock, AlertTriangle, ArrowRight, User, X, Phone, MessageSquare } from 'lucide-react';
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const PRIORITY_STYLES = {
  urgent: { bg: 'bg-red-500/20', border: 'border-red-500/30', text: 'text-red-400', label: 'Urgent' },
  normal: { bg: 'bg-blue-500/20', border: 'border-blue-500/30', text: 'text-blue-400', label: 'Normal' },
  basse: { bg: 'bg-slate-500/20', border: 'border-slate-500/30', text: 'text-slate-400', label: 'Basse' }
};

const STATUS_LABELS = { a_faire: 'A faire', en_cours: 'En cours', terminee: 'Terminee' };

const TaskNotificationBadge = ({ userEmail, onOpenTasks }) => {
  const [taskCount, setTaskCount] = useState(0);
  const [rappelCount, setRappelCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState('taches'); // 'taches' | 'rappels'
  const [recentTasks, setRecentTasks] = useState([]);
  const [rappels, setRappels] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const totalCount = taskCount + rappelCount;

  // Poll counts every 30s
  useEffect(() => {
    if (!userEmail) return;
    const fetchCounts = () => {
      authFetch(`${API}/tasks/notifications/count?user_email=${encodeURIComponent(userEmail)}`)
        .then(r => r.json())
        .then(d => setTaskCount(d.count || 0))
        .catch(() => {});
      authFetch(`${API}/webhook/rappels/aujourdhui?telepro_email=${encodeURIComponent(userEmail)}`)
        .then(r => r.json())
        .then(d => setRappelCount((d.rappels || []).length))
        .catch(() => {});
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [userEmail]);

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openDropdown = async () => {
    setShowDropdown(!showDropdown);
    if (!showDropdown) {
      loadData();
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [tasksRes, rappelsRes] = await Promise.all([
        authFetch(`${API}/tasks?assigned_to=${encodeURIComponent(userEmail)}&include_created=true&limit=8`),
        authFetch(`${API}/webhook/rappels/semaine?telepro_email=${encodeURIComponent(userEmail)}`)
      ]);
      const tasksData = await tasksRes.json();
      const rappelsData = await rappelsRes.json();
      setRecentTasks(tasksData.tasks || []);
      setRappels(rappelsData.rappels || []);
    } catch {
      setRecentTasks([]);
      setRappels([]);
    }
    setLoading(false);
  };

  const markRead = async (taskId) => {
    await authFetch(`${API}/tasks/${taskId}/read?user_email=${encodeURIComponent(userEmail)}`, { method: 'POST' });
    setTaskCount(prev => Math.max(0, prev - 1));
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'maintenant';
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}j`;
  };

  const formatRappelDate = (date, heure) => {
    if (!date) return '';
    const d = new Date(date);
    const jours = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
    const isTomorrow = d.toDateString() === tomorrow.toDateString();
    const prefix = isToday ? "Aujourd'hui" : isTomorrow ? 'Demain' : `${jours[d.getDay()]} ${d.getDate()}/${d.getMonth()+1}`;
    return heure ? `${prefix} ${heure}` : prefix;
  };

  return (
    <div ref={ref} className="relative" data-testid="task-notification-badge">
      <button
        onClick={openDropdown}
        className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 hover:border-blue-500/50 transition-colors"
        data-testid="task-bell-btn"
      >
        <Bell className={`w-5 h-5 ${totalCount > 0 ? 'text-blue-400' : 'text-slate-400'}`} />
        {totalCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="task-badge-count">
            {totalCount > 9 ? '9+' : totalCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 top-12 w-96 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="task-dropdown">
          {/* Header avec tabs */}
          <div className="border-b border-slate-700">
            <div className="p-2 flex items-center justify-between">
              <div className="flex gap-1">
                <button
                  onClick={() => setActiveTab('taches')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                    activeTab === 'taches' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="tab-taches"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  Tâches
                  {taskCount > 0 && <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded-full text-xs">{taskCount}</span>}
                </button>
                <button
                  onClick={() => setActiveTab('rappels')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                    activeTab === 'rappels' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="tab-rappels"
                >
                  <Phone className="w-3.5 h-3.5" />
                  Rappels clients
                  {rappelCount > 0 && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded-full text-xs">{rappelCount}</span>}
                </button>
              </div>
              <button onClick={() => setShowDropdown(false)} className="text-slate-400 hover:text-white p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="max-h-80 overflow-y-auto">
            {loading && <p className="p-4 text-center text-slate-500 text-sm">Chargement...</p>}

            {/* Tab Tâches */}
            {!loading && activeTab === 'taches' && (
              <>
                {recentTasks.length === 0 && (
                  <p className="p-4 text-center text-slate-500 text-sm">Aucune tâche</p>
                )}
                {recentTasks.map(task => {
                  const isUnread = !task.read_by?.includes(userEmail);
                  const prio = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.normal;
                  return (
                    <div
                      key={task.task_id}
                      onClick={() => { markRead(task.task_id); if (onOpenTasks) onOpenTasks(task); }}
                      className={`p-3 border-b border-slate-800 cursor-pointer hover:bg-slate-800/50 transition-colors ${isUnread ? 'bg-blue-500/5' : ''}`}
                      data-testid={`task-item-${task.task_id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {isUnread && <span className="w-2 h-2 bg-blue-400 rounded-full shrink-0" />}
                            <span className="text-sm text-white font-medium truncate">{task.title}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${prio.bg} ${prio.text}`}>{prio.label}</span>
                            <span className="text-xs text-slate-500">{STATUS_LABELS[task.status] || task.status}</span>
                            {task.client_name && (
                              <span className="text-xs text-slate-500 flex items-center gap-1">
                                <User className="w-3 h-3" />{task.client_name}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-slate-600 shrink-0">{timeAgo(task.updated_at || task.created_at)}</span>
                      </div>
                      {task.thread?.length > 0 && (
                        <p className="text-xs text-slate-500 mt-1 truncate pl-4">
                          {task.thread[task.thread.length - 1]?.author_name || 'Quelqu\'un'}: {task.thread[task.thread.length - 1]?.text}
                        </p>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            {/* Tab Rappels */}
            {!loading && activeTab === 'rappels' && (
              <>
                {rappels.length === 0 && (
                  <p className="p-4 text-center text-slate-500 text-sm">Aucun rappel prévu</p>
                )}
                {rappels.map((rappel, idx) => {
                  const isOverdue = rappel.date_rappel && new Date(`${rappel.date_rappel}T${rappel.heure_rappel || '00:00'}`) < new Date();
                  return (
                    <div
                      key={rappel.lead_id || idx}
                      className={`p-3 border-b border-slate-800 cursor-pointer hover:bg-slate-800/50 transition-colors ${isOverdue ? 'bg-red-500/5' : ''}`}
                      data-testid={`rappel-item-${idx}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {isOverdue && <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                            <span className="text-sm text-white font-medium truncate">
                              {rappel.prenom || ''} {rappel.nom || 'Lead inconnu'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${isOverdue ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                              {formatRappelDate(rappel.date_rappel, rappel.heure_rappel)}
                            </span>
                            {rappel.telephone && (
                              <span className="text-xs text-slate-500 flex items-center gap-1">
                                <Phone className="w-3 h-3" />{rappel.telephone}
                              </span>
                            )}
                          </div>
                        </div>
                        {rappel.nrp_count > 0 && (
                          <span className="text-xs px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded shrink-0">NRP {rappel.nrp_count}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Footer */}
          {onOpenTasks && (
            <button
              onClick={() => { setShowDropdown(false); onOpenTasks(null); }}
              className="w-full p-3 bg-slate-800/30 border-t border-slate-700 text-sm text-blue-400 hover:text-blue-300 flex items-center justify-center gap-2"
              data-testid="task-see-all"
            >
              Voir tout <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export { TaskNotificationBadge };
export default TaskNotificationBadge;
